import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Headshot, HeadshotKeys, MediaSize, RegistrantKeys } from '@ag-common-lib/public-api';
import { normalizeLog } from '../../../utils/normalize-log';
import { ChangeHistory, ChangeHistoryKey } from '../../../change-history.model';

@Component({
  selector: 'ag-shr-change-log-headshot-detail',
  templateUrl: './change-log-headshot.component.html',
  styleUrls: ['./change-log-headshot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeLogHeadshotComponent {
  @Input()
  set log(item: ChangeHistory) {
    const headshotLog = item?.[ChangeHistoryKey.log];
    const headshotOldData = item?.[ChangeHistoryKey.oldData];
    const cropperPayload = headshotLog?.[HeadshotKeys.imageCropperPayload];
    const isCropperPayloadArray = Array.isArray(cropperPayload);
    this.isCropped = isCropperPayloadArray ? !!cropperPayload?.filter(Boolean).length : !!cropperPayload;
    const fromRelativeX1 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeX1?.[0]
      : cropperPayload?.[0]?.relativeCropperPosition?.relativeX1;
    const fromRelativeX2 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeX2?.[0]
      : cropperPayload?.[0]?.relativeCropperPosition?.relativeX2;
    const fromRelativeY1 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeY1?.[0]
      : cropperPayload?.[0]?.relativeCropperPosition?.relativeY1;
    const fromRelativeY2 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeY2?.[0]
      : cropperPayload?.[0]?.relativeCropperPosition?.relativeY2;
    const toRelativeX1 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeX1?.[1]
      : cropperPayload?.[1]?.relativeCropperPosition?.relativeX1;
    const toRelativeX2 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeX2?.[1]
      : cropperPayload?.[1]?.relativeCropperPosition?.relativeX2;
    const toRelativeY1 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeY1?.[1]
      : cropperPayload?.[1]?.relativeCropperPosition?.relativeY1;
    const toRelativeY2 = !isCropperPayloadArray
      ? cropperPayload?.relativeCropperPosition?.relativeY2?.[1]
      : cropperPayload?.[1]?.relativeCropperPosition?.relativeY2;

    const headshotWasabiPath = headshotLog?.[HeadshotKeys.wasabiPath];
    // Normalize wasabi path changes to support cases like:
    // - [old, new]
    // - [new] (single-element provided; treat as "to")
    // - [new, 0, 0] (jsondiffpatch add marker; treat index 0 as "to")
    // - undefined in log but present in oldData
    let fromHeadshotWasabiPath =
      Array.isArray(headshotWasabiPath) ? headshotWasabiPath?.[0] : undefined;
    let toHeadshotWasabiPath =
      Array.isArray(headshotWasabiPath) ? headshotWasabiPath?.[1] : undefined;

    // If only one element is provided, treat it as the new value
    if (Array.isArray(headshotWasabiPath) && headshotWasabiPath.length === 1) {
      toHeadshotWasabiPath = headshotWasabiPath[0];
      fromHeadshotWasabiPath = headshotOldData?.[HeadshotKeys.wasabiPath] ?? null;
    }

    // If array looks like [new, 0, 0] => jsondiffpatch "add" marker
    if (
      Array.isArray(headshotWasabiPath) &&
      headshotWasabiPath.length >= 3 &&
      headshotWasabiPath?.[1] === 0 &&
      headshotWasabiPath?.[2] === 0
    ) {
      toHeadshotWasabiPath = headshotWasabiPath[0];
      fromHeadshotWasabiPath = headshotOldData?.[HeadshotKeys.wasabiPath] ?? null;
    }

    if (fromHeadshotWasabiPath == null) {
      fromHeadshotWasabiPath = headshotOldData?.[HeadshotKeys.wasabiPath] ?? null;
    }
    if (toHeadshotWasabiPath == null) {
      toHeadshotWasabiPath = headshotOldData?.[HeadshotKeys.wasabiPath] ?? null;
    }

    if (!!toHeadshotWasabiPath) {
      this.fromHeadshot = {
        [HeadshotKeys.wasabiPath]: fromHeadshotWasabiPath,
        [HeadshotKeys.imageCropperPayload]: {
          relativeCropperPosition: {
            relativeX1: fromRelativeX1 ?? null,
            relativeX2: fromRelativeX2 ?? null,
            relativeY1: fromRelativeY1 ?? '0',
            relativeY2: fromRelativeY2 ?? '1',
          },
        },
      };

      this.toHeadshot = {
        [HeadshotKeys.wasabiPath]: toHeadshotWasabiPath,
        [HeadshotKeys.imageCropperPayload]: {
          relativeCropperPosition: {
            relativeX1: toRelativeX1 ?? null,
            relativeX2: toRelativeX2 ?? null,
            relativeY1: toRelativeY1 ?? '0',
            relativeY2: toRelativeY2 ?? '1',
          },
        },
      };
    }
  }

  fromHeadshot: Headshot;
  toHeadshot: Headshot;
  isCropped = false;

  protected readonly normalizeLog = normalizeLog;
  protected readonly HeadshotKeys = HeadshotKeys;
  protected readonly RegistrantKeys = RegistrantKeys;
  protected readonly MediaSize = MediaSize;
}
